// frontend/src/pages/TestReport.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showError } from '../utils/toastUtils';
import api from '../services/api';

const TestReport = () => {
    const { accessToken } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);
    const [session, setSession] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        if (accessToken) {
            fetchReport();
        }
    }, [accessToken]);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/test/report/${accessToken}`);
            const data = response.data;

            if (data.success) {
                setReport(data.report);
                setSession(data.session);
            } else {
                showError(data.error || 'Failed to fetch report');
            }
        } catch (error) {
            console.error('Error fetching report:', error);
            showError(error.response?.data?.error || 'Failed to fetch report');
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const getScoreColor = (tScore) => {
        if (tScore < 40) return 'text-blue-600';
        if (tScore < 60) return 'text-green-600';
        return 'text-purple-600';
    };

    const getScoreLevel = (tScore) => {
        if (tScore < 35) return { label: 'Very Low', color: 'bg-red-500' };
        if (tScore < 45) return { label: 'Low', color: 'bg-orange-500' };
        if (tScore < 55) return { label: 'Average', color: 'bg-yellow-500' };
        if (tScore < 65) return { label: 'High Average', color: 'bg-green-500' };
        if (tScore < 75) return { label: 'High', color: 'bg-blue-500' };
        return { label: 'Very High', color: 'bg-purple-500' };
    };

    // Factor groups for EBCSS
    const factorGroups = {
        'ESST-1': { label: 'Emotional Stability & Stress Tolerance', color: '#4F46E5' },
        'IEB-2': { label: 'Integrity & Ethical Behaviour', color: '#059669' },
        'RCD-3': { label: 'Rule Compliance & Work Discipline', color: '#D97706' },
        'WCR-4': { label: 'Work Commitment & Reliability', color: '#7C3AED' },
        'SBAC-5': { label: 'Social Behaviour & Aggression Control', color: '#DC2626' },
        'AMLC-6': { label: 'Adaptability to Migrant Living Conditions', color: '#0891B2' },
        'IFTLA-7': { label: 'Instruction Following & Task Learning Ability', color: '#2563EB' }
    };

    const domainMap = {
        'emotional_stability_stress_tolerance': 'Emotional Stability & Stress Tolerance',
        'integrity_ethical_behaviour': 'Integrity & Ethical Behaviour',
        'rule_compliance_work_discipline': 'Rule Compliance & Work Discipline',
        'work_commitment_reliability': 'Work Commitment & Reliability',
        'social_behaviour_aggression_control': 'Social Behaviour & Aggression Control',
        'adaptability_migrant_living_conditions': 'Adaptability to Migrant Living Conditions',
        'instruction_following_task_learning_ability': 'Instruction Following & Task Learning Ability'
    };

    // Get factor scores
    const getFactorScores = () => {
        const scales = report?.scales || {};
        const factorScores = {};

        for (const [factorId, factorInfo] of Object.entries(factorGroups)) {
            if (scales[factorId]) {
                factorScores[factorId] = {
                    ...scales[factorId],
                    label: factorInfo.label,
                    color: factorInfo.color
                };
            }
        }
        return factorScores;
    };

    // Get facets (sub-scales)
    const getFacets = () => {
        const scales = report?.scales || {};
        const facets = {};
        for (const [scaleId, score] of Object.entries(scales)) {
            if (factorGroups[scaleId]) continue;
            if (scaleId.includes('-')) {
                facets[scaleId] = score;
            }
        }
        return facets;
    };

    const factorScores = getFactorScores();
    const facets = getFacets();

    // ============================================
    // CALCULATE OVERALL SCORE - MOVED OUTSIDE JSX
    // ============================================
    const scores = Object.values(factorScores).map(s => s.tScore || 50);
    const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 50;
    const overallLevel = getScoreLevel(avgScore);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff9f2f] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading report...</p>
                </div>
            </div>
        );
    }

    if (!report) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="text-center">
                    <p className="text-gray-600">Report not found</p>
                    <button
                        onClick={() => navigate('/admin')}
                        className="mt-4 px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a]"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const isEBCSS = report.assessmentId?.startsWith('EBCSS-') ||
        Object.keys(report.scales || {}).some(key => key.includes('ESST-') || key.includes('IEB-'));

    return (
        <div className="min-h-screen bg-[#f4f6fb] py-8">
            <div className="max-w-4xl mx-auto px-4">
                {/* Assessment Type Badge */}
                <div className="mb-4 flex justify-center">
                    <span className="px-4 py-1 bg-[#063b56] text-white text-sm rounded-full">
                        {isEBCSS ? 'EBCSS Report' : 'Assessment Report'}
                    </span>
                </div>

                {/* Header */}
                <div className="bg-[#063b56] rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                    <h1 className="text-2xl font-bold text-white mb-2">
                        {isEBCSS ? 'EBCSS Assessment Report' : 'Assessment Report'}
                    </h1>
                    <p className="text-gray-300">
                        <strong className="text-white">Name:</strong> {report.candidate?.name || 'N/A'}<br />
                        <strong className="text-white">Email:</strong> {report.candidate?.email || 'N/A'}<br />
                        <strong className="text-white">Assessment:</strong> {report.assessmentId || 'N/A'}<br />
                        <strong className="text-white">Completed:</strong> {report.completedAt ? new Date(report.completedAt).toLocaleString() : 'N/A'}
                    </p>
                    {session && (
                        <p className="text-gray-400 text-sm mt-1">
                            Session ID: {session.id}
                        </p>
                    )}
                </div>

                {/* ============================================ */}
                {/* SECTION 1: Validity Indices */}
                {/* ============================================ */}
                {report.validity && Object.keys(report.validity).length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                        <h2 className="text-xl font-bold text-[#063b56] mb-4">📋 Validity Indices</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.entries(report.validity).map(([key, value]) => (
                                <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                                    <div className="text-sm text-gray-500 capitalize">
                                        {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
                                    </div>
                                    <div className={`text-xl font-bold ${getScoreColor(value?.tScore || 50)}`}>
                                        {value?.tScore || '-'}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        {value?.tScore > 60 ? '⚠️ Elevated' : '✓ Normal'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ============================================ */}
                {/* SECTION 2: Factor Scores */}
                {/* ============================================ */}
                {Object.keys(factorScores).length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                        <h2 className="text-xl font-bold text-[#063b56] mb-4">📊 Factor Scores</h2>
                        <div className="space-y-4">
                            {Object.entries(factorScores).map(([factorId, score]) => {
                                const level = getScoreLevel(score.tScore || 50);
                                return (
                                    <div key={factorId}>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="font-medium text-gray-700">{score.label}</span>
                                            <div className="flex items-center space-x-3">
                                                <span className={`text-sm font-bold ${getScoreColor(score.tScore || 50)}`}>
                                                    T-Score: {score.tScore || '-'}
                                                </span>
                                                <span className={`text-xs px-2 py-1 rounded-full text-white ${level.color}`}>
                                                    {level.label}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div
                                                className={`rounded-full h-3 transition-all duration-500`}
                                                style={{
                                                    width: `${Math.min(100, ((score.tScore || 50) / 80) * 100)}%`,
                                                    backgroundColor: score.color || '#ff9f2f'
                                                }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                                            <span>Raw Score: {score.rawScore || 0}</span>
                                            <span>Percentile: {score.percentile || 'N/A'}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ============================================ */}
                {/* SECTION 3: Facets (Sub-scales) */}
                {/* ============================================ */}
                {Object.keys(facets).length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                        <h2 className="text-xl font-bold text-[#063b56] mb-4">📋 Detailed Facet Scores</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Object.entries(facets).map(([facetId, score]) => {
                                const level = getScoreLevel(score.tScore || 50);
                                const facetLabel = facetId.replace(/^[A-Z]+-/, '').replace(/-/g, ' ');
                                return (
                                    <div key={facetId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="font-medium text-gray-700 text-sm capitalize">
                                                {facetLabel}
                                            </span>
                                            <span className={`text-sm font-bold ${getScoreColor(score.tScore || 50)}`}>
                                                {score.tScore || '-'}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-[#ff9f2f] rounded-full h-2 transition-all"
                                                style={{ width: `${Math.min(100, ((score.tScore || 50) / 80) * 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ============================================ */}
                {/* SECTION 4: Domain Scores */}
                {/* ============================================ */}
                {report.domains && Object.keys(report.domains).length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                        <h2 className="text-xl font-bold text-[#063b56] mb-4">📂 Domain Scores</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Object.entries(report.domains).map(([domainKey, domain]) => {
                                const domainName = domainMap[domainKey] || domainKey.replace(/_/g, ' ');
                                const factors = Object.values(domain);
                                const scores = factors.filter(s => s && s.tScore !== undefined).map(s => s.tScore);
                                const avgDomainScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
                                const level = getScoreLevel(avgDomainScore);

                                return (
                                    <div key={domainKey} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="font-medium text-gray-700">{domainName}</span>
                                            <span className={`text-sm font-bold ${getScoreColor(avgDomainScore)}`}>
                                                Avg: {avgDomainScore.toFixed(1)}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                            <div
                                                className="rounded-full h-2 transition-all"
                                                style={{
                                                    width: `${Math.min(100, (avgDomainScore / 80) * 100)}%`,
                                                    backgroundColor: level.color.replace('bg-', '')
                                                }}
                                            />
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {Object.entries(domain).map(([factor, value]) => {
                                                if (value && value.tScore !== undefined) {
                                                    return (
                                                        <span key={factor} className="inline-block mr-2">
                                                            {factor}: {value.tScore}
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ============================================ */}
                {/* SECTION 5: Interpretations */}
                {/* ============================================ */}
                {report.interpretations && Object.keys(report.interpretations).length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                        <h2 className="text-xl font-bold text-[#063b56] mb-4">📖 Interpretations</h2>
                        <div className="space-y-4">
                            {Object.entries(report.interpretations).map(([scale, interp]) => {
                                const score = report.scales?.[scale];
                                const tScore = score?.tScore || 'N/A';
                                const level = score ? getScoreLevel(score.tScore) : { label: 'N/A', color: 'bg-gray-500' };
                                const isExpanded = expandedSections[scale] || false;

                                let factorLabel = scale;
                                for (const [fId, info] of Object.entries(factorGroups)) {
                                    if (scale.includes(fId) || fId.includes(scale)) {
                                        factorLabel = info.label;
                                        break;
                                    }
                                }

                                const text = interp.interpretation_text ||
                                    interp.low_score_interpretation ||
                                    interp.average_score_interpretation ||
                                    interp.high_score_interpretation ||
                                    interp.summary_narrative ||
                                    'No interpretation available';

                                return (
                                    <div key={scale} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                            onClick={() => toggleSection(scale)}
                                            className="w-full p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                                        >
                                            <div>
                                                <span className="font-semibold text-gray-800">{factorLabel}</span>
                                                <span className={`ml-3 text-xs px-2 py-1 rounded-full text-white ${level.color}`}>
                                                    {level.label}
                                                </span>
                                                <span className="ml-2 text-sm text-gray-500">T-Score: {tScore}</span>
                                            </div>
                                            <span className="text-gray-400">
                                                {isExpanded ? '▲' : '▼'}
                                            </span>
                                        </button>
                                        {isExpanded && (
                                            <div className="p-4 space-y-3">
                                                <div>
                                                    <p className="text-sm text-gray-700">{text}</p>
                                                </div>
                                                {interp.summary_narrative && (
                                                    <div>
                                                        <p className="text-sm text-gray-700 font-medium">Summary:</p>
                                                        <p className="text-gray-600 text-sm">{interp.summary_narrative}</p>
                                                    </div>
                                                )}
                                                {interp.strengths && (
                                                    <div>
                                                        <p className="text-sm text-gray-700 font-medium text-green-600">✓ Strengths:</p>
                                                        <p className="text-gray-600 text-sm">{interp.strengths}</p>
                                                    </div>
                                                )}
                                                {interp.development_areas && (
                                                    <div>
                                                        <p className="text-sm text-gray-700 font-medium text-orange-600">⚡ Development Areas:</p>
                                                        <p className="text-gray-600 text-sm">{interp.development_areas}</p>
                                                    </div>
                                                )}
                                                {interp.recommendations && (
                                                    <div>
                                                        <p className="text-sm text-gray-700 font-medium text-blue-600">💡 Recommendations:</p>
                                                        <p className="text-gray-600 text-sm">{interp.recommendations}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ============================================ */}
                {/* SECTION 6: Overall Summary - USING avgScore */}
                {/* ============================================ */}
                {Object.keys(factorScores).length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                        <h2 className="text-xl font-bold text-[#063b56] mb-4">📈 Overall Summary</h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="bg-blue-50 rounded-lg p-4 text-center">
                                <p className="text-sm text-gray-500">Overall Score</p>
                                <p className={`text-2xl font-bold ${getScoreColor(avgScore)}`}>
                                    {Math.round(avgScore)}
                                </p>
                                <p className="text-xs text-gray-400">Average T-Score</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-4 text-center">
                                <p className="text-sm text-gray-500">Overall Level</p>
                                <p className={`text-2xl font-bold`} style={{ color: overallLevel.color.replace('bg-', '') }}>
                                    {overallLevel.label}
                                </p>
                                <p className="text-xs text-gray-400">Based on average score</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-4 text-center">
                                <p className="text-sm text-gray-500">Factors Assessed</p>
                                <p className="text-2xl font-bold text-purple-600">
                                    {Object.keys(factorScores).length}
                                </p>
                                <p className="text-xs text-gray-400">Total factors evaluated</p>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <p className="text-sm text-gray-700">
                                <strong>Summary:</strong> The candidate demonstrates {overallLevel.label.toLowerCase()}
                                overall performance across the assessed dimensions. {overallLevel.label === 'High' || overallLevel.label === 'Very High' ?
                                    'This suggests strong suitability with good work ethic and interpersonal skills.' :
                                    overallLevel.label === 'Average' ?
                                        'This suggests adequate suitability with room for development in specific areas.' :
                                        'This suggests the candidate may benefit from additional training and support in key areas.'}
                            </p>
                        </div>
                    </div>
                )}

                {/* ============================================ */}
                {/* SECTION 7: Raw Scores */}
                {/* ============================================ */}
                {report.rawScores && Object.keys(report.rawScores).length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                        <h2 className="text-xl font-bold text-[#063b56] mb-4">📊 Raw Scores</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {Object.entries(report.rawScores)
                                .filter(([key]) => key.includes('-'))
                                .slice(0, 12)
                                .map(([key, value]) => (
                                    <div key={key} className="bg-gray-50 rounded-lg p-2 text-center">
                                        <span className="text-xs text-gray-500 block">{key}</span>
                                        <span className="text-sm font-bold text-[#063b56]">{value}</span>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-center space-x-4">
                    <button
                        onClick={() => navigate('/admin')}
                        className="px-4 py-2 bg-[#063b56] text-white rounded-lg hover:bg-[#0a4a6a]"
                    >
                        Back to Dashboard
                    </button>
                    <button
                        onClick={() => window.print()}
                        className="px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a]"
                    >
                        🖨️ Print Report
                    </button>
                </div>

                {/* Footer */}
                <div className="mt-8 text-center text-xs text-gray-400">
                    <p>This report is generated based on the candidate's responses.</p>
                    <p>© {new Date().getFullYear()} Assessment Platform. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default TestReport;